import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { Space } from "antd";
import { DigestV2State } from "pages/api/deep-links/types";
import { AnnotatedDeepLink } from "pages/api/deep-links/types";
import { StatusIndicatorSection } from "./components/StatusIndicator";
import { useOrg } from "src/hooks/useOrg";
import React, { useState, useEffect } from "react";
import {
  DigestDataLinks,
  DigestLinks,
} from "pages/[orgId]/teams/components/ReportLinks";
import removeMd from "remove-markdown";
import { BlockConfig, Block } from "./types";
import { BlockRendererProps } from "./types";

/**
 * Strips markdown syntax from text while preserving the content
 * @param text The text that may contain markdown
 * @returns The text with markdown syntax removed
 */
export const stripMarkdown = (text: string): string => {
  if (!text) return "";
  return removeMd(text).trim();
};

export const formatHeadline = (data: any, maxWords: number = 10) => {
  if (!data) return "";

  // Ensure maxWords has a valid value
  const wordCount = !maxWords || maxWords < 1 ? 10 : maxWords;
  if (data && typeof data === "string") {
    return stripMarkdown(data).split(" ").slice(0, wordCount).join(" ") + "...";
  }
  //if data has a headline, then return it, else, get the first 10 words of the highlights, or then the first 10 words of the summary
  if (data.headline) {
    return stripMarkdown(data.headline);
  } else if (data.summary && typeof data.summary === "string") {
    return (
      stripMarkdown(data.summary).split(" ").slice(0, wordCount).join(" ") +
      "..."
    );
  } else if (data.summary && typeof data.summary === "object") {
    return (
      stripMarkdown(data.summary.description)
        .split(" ")
        .slice(0, wordCount)
        .join(" ") + "..."
    );
  } else if (data.notable_patterns) {
    return (
      stripMarkdown(data.notable_patterns)
        .split(" ")
        .slice(0, wordCount)
        .join(" ") + "..."
    );
  } else {
    return "";
  }
};
export const FormatAdditionalResources: React.FC<{
  links: any[];
  data: AnnotatedDeepLink<DigestV2State>;
  displayTitle?: boolean;
}> = ({ links, data, displayTitle = false }) => {
  const orgId = useOrg().id!;
  const hasContent =
    (links && links.length > 0) ||
    (data.json?.data && Object.keys(data.json.data).length > 0);

  if (!hasContent) {
    return <React.Fragment />;
  }

  return (
    <StatusIndicatorSection title="Additional Resources" tooltip={false}>
      <Space direction="vertical" style={{ width: "100%", gap: 0 }}>
        <DigestLinks
          title={data?.name}
          orgId={orgId}
          link={data}
          filters={[]}
          displayTitle={displayTitle}
        />
        {(() => {
          const dataLinks = DigestDataLinks(orgId, data, [], false);
          if (!dataLinks.items || dataLinks.items.length === 0) {
            return <React.Fragment />;
          }
          return (
            <div>
              {dataLinks.items.map(
                (item, index) =>
                  item &&
                  "label" in item &&
                  item.label && <div key={index}>{item.label}</div>
              )}
            </div>
          );
        })()}
      </Space>
    </StatusIndicatorSection>
  );
};

export const isValidPercent = (
  percent: string | number | undefined
): boolean => {
  if (!percent) return false;
  const numericValue =
    typeof percent === "string" ? Number.parseFloat(percent) : percent;
  return (
    !Number.isNaN(numericValue) && numericValue >= -100 && numericValue <= 100
  );
};

const colors = [
  "var(--color-sky-1)",
  "var(--color-green-1)",
  "var(--color-tapestry-1)",
  "var(--color-yellow-1)",
  "var(--color-alto-1)",
  "var(--color-ebony-1)",
];

// Create a seeded random number generator based on the current date
const getSeededRandom = (seed: number) => {
  const today = new Date();
  const randomSeed =
    today.getFullYear() * 10_000 +
    (today.getMonth() + 1) * 100 +
    today.getDate();
  return () => {
    const x = Math.sin(randomSeed) * seed;
    return x - Math.floor(x);
  };
};

export const shuffledColors = (seed: number | string) => {
  if (typeof seed === "string") {
    seed = [...seed].reduce((acc, char) => acc + char.codePointAt(0)!, 0);
  }
  const rand = getSeededRandom(seed);
  const arr = [...colors];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export const getChangeDirection = (
  changeValue?: number | string
): "increase" | "decrease" | "neutral" | "" => {
  if (typeof changeValue !== "number" && typeof changeValue !== "string")
    return "";
  if (typeof changeValue === "string") {
    const numericValue = Number.parseFloat(changeValue);
    if (Number.isNaN(numericValue)) return "";
    changeValue = numericValue;
  }
  if (typeof changeValue !== "number") return "";
  if (changeValue > 0) return "increase";
  if (changeValue < 0) return "decrease";
  if (changeValue === 0) return "neutral";
  return "";
};

export const getChangeIcon = (
  changeType?: "increase" | "decrease" | "neutral" | ""
) => {
  switch (changeType) {
    case "increase": {
      return <ArrowUpOutlined style={{ color: "var(--color-tertiary)" }} />;
    }
    case "decrease": {
      return (
        <ArrowDownOutlined style={{ color: "var(--color-text-tertiary)" }} />
      );
    }
    case "neutral": {
      return <MinusOutlined style={{ color: "var(--color-text-tertiary)" }} />;
    }
    default: {
      return <></>;
    }
  }
};

export const convertBlockConfigsToMap = (
  blockConfigs: BlockConfig[],
  getBlock: (id: string) => Block | undefined
): Map<string, Block> => {
  return new Map(
    blockConfigs
      .map((config: BlockConfig) => {
        const block = getBlock(config.id);
        if (!block) {
          console.warn(`Block ${config.id} not found`);
          return null;
        }
        return [
          config.id,
          {
            ...block,
            variants: block.variants,
            renderer: (props: BlockRendererProps) =>
              block.renderer({
                ...props,
                variant: config.variant || block.variants[0]?.id || "default",
                settings: {
                  ...block.variants[0]?.defaultSettings,
                  ...config.settings,
                },
                className: config.className,
                style: config.style,
              }),
          },
        ] as [string, Block];
      })
      .filter((entry): entry is [string, Block] => entry !== null)
  );
};

export function useDrawerWidth() {
  const [drawerWidth, setDrawerWidth] = useState(() =>
    Math.min(window.innerWidth * 0.7, 1200)
  );

  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(Math.min(window.innerWidth * 0.7, 1200));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return drawerWidth;
}
